import { Progress } from '@/components/ui/progress';
import { Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface AuditStep {
  id: string;
  name: string;
  description: string;
  estimatedDuration: number; // in seconds
  status: 'pending' | 'running' | 'completed' | 'error';
  actualDuration?: number;
}

interface AuditProgressBarProps {
  currentStep: string;
  steps: AuditStep[];
  startTime: number;
  onCancel?: () => void;
}

export function AuditProgressBar({ currentStep, steps, startTime, onCancel }: AuditProgressBarProps) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [smartProgress, setSmartProgress] = useState(0);

  // Update elapsed time every second
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Calculate smart progress based on elapsed time and typical performance
  useEffect(() => {
    const totalExpectedTime = steps.reduce((sum, step) => sum + step.estimatedDuration, 0);
    const timeBasedProgress = Math.min(90, (elapsedTime / totalExpectedTime) * 100);

    // Add some realistic step-based progress
    const currentStepIndex = steps.findIndex(step => step.id === currentStep);
    const stepBasedProgress = currentStepIndex >= 0 ? (currentStepIndex / steps.length) * 100 : 0;

    // Blend time-based and step-based progress for more realism
    const blendedProgress = Math.max(stepBasedProgress, timeBasedProgress * 0.7 + stepBasedProgress * 0.3);

    setSmartProgress(Math.min(95, blendedProgress));
  }, [elapsedTime, currentStep, steps]);

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);
  const currentStepInfo = steps[currentStepIndex];
  const totalSteps = steps.length;

  // Calculate remaining time estimate
  const remainingSteps = steps.slice(currentStepIndex + 1);
  const remainingTime = remainingSteps.reduce((sum, step) => sum + step.estimatedDuration, 0);
  const currentStepRemaining = currentStepInfo ? Math.max(0, currentStepInfo.estimatedDuration - elapsedTime) : 0;
  const totalRemainingTime = currentStepRemaining + remainingTime;
  
  // Format time helper
  const formatTime = (seconds: number) => {
    // Validate input and handle edge cases
    if (!seconds || seconds < 0 || !isFinite(seconds)) return '0s';
    if (seconds > 3600) return '>1h'; // Cap at 1 hour for display

    const safeSeconds = Math.floor(seconds);
    if (safeSeconds < 60) return `${safeSeconds}s`;

    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStepIcon = (status: AuditStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
      case 'running':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />;
    }
  };

  return (
    <div className="bg-background border border-border rounded-lg p-6 shadow-sm">
      {/* Progress Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
          <div>
            <h3 className="font-semibold text-foreground">Running AI Analysis</h3>
            <p className="text-sm text-muted-foreground">
              Step {currentStepIndex + 1} of {totalSteps}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{formatTime(elapsedTime)} elapsed</span>
          </div>
          {totalRemainingTime > 0 && (
            <div>~{formatTime(totalRemainingTime)} remaining</div>
          )}
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className="mb-6">
        <Progress value={smartProgress} className="h-2 mb-2" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{Math.round(smartProgress)}% complete</span>
          <span>Step {currentStepIndex + 1} of {totalSteps}</span>
        </div>
      </div>

      {/* Current Step Details */}
      {steps[currentStepIndex] && (
        <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            <span className="font-medium text-blue-900">
              {steps[currentStepIndex].name}
            </span>
            <span className="text-xs text-blue-600">
              (~{currentStepInfo?.estimatedDuration || 0}s)
            </span>
          </div>
          <p className="text-sm text-blue-700">
            {steps[currentStepIndex].description}
          </p>
        </div>
      )}

      {/* Steps Timeline */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-foreground mb-3">Analysis Steps</h4>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`flex items-center gap-3 p-2 rounded text-sm ${
                step.status === 'running' ? 'bg-blue-50' :
                step.status === 'completed' ? 'bg-emerald-50' :
                step.status === 'error' ? 'bg-red-50' :
                'bg-gray-50'
              }`}
            >
              {getStepIcon(step.status)}
              <div className="flex-1">
                <div className="font-medium">
                  {step.name}
                </div>
                {step.status === 'running' && (
                  <div className="text-xs text-blue-600">
                    {step.description}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                ~{step.estimatedDuration}s
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cancel Button */}
      {onCancel && (
        <div className="mt-4 text-center">
          <button
            onClick={onCancel}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel Analysis
          </button>
        </div>
      )}
    </div>
  );
}

// Default audit steps configuration
// Function to get steps with adjusted timings for browserless analysis
export const getAuditSteps = (fullAnalysis = false): AuditStep[] => {
  const baseSteps = DEFAULT_AUDIT_STEPS;
  if (!fullAnalysis) return baseSteps;
  
  // Adjust timings for browserless.io analysis
  return baseSteps.map(step => {
    if (step.id === 'ai-optimization') {
      return {
        ...step,
        name: 'Enhanced AI Analysis (browserless.io)',
        description: 'Running deep JavaScript analysis with browserless.io rendering',
        estimatedDuration: 90 // Much longer for browserless analysis
      };
    }
    if (step.id === 'fetch') {
      return { ...step, estimatedDuration: 8 }; // Longer initial fetch for JS sites
    }
    return step;
  });
};

export const DEFAULT_AUDIT_STEPS: AuditStep[] = [
  {
    id: 'fetch',
    name: 'Fetching Page Content',
    description: 'Downloading HTML and checking basic connectivity',
    estimatedDuration: 3,
    status: 'pending'
  },
  {
    id: 'seo',
    name: 'SEO Analysis',
    description: 'Analyzing titles, headings, meta tags, and content structure',
    estimatedDuration: 4,
    status: 'pending'
  },
  {
    id: 'performance',
    name: 'Performance Analysis',
    description: 'Measuring load times and technical quality metrics',
    estimatedDuration: 3,
    status: 'pending'
  },
  {
    id: 'schema',
    name: 'Structured Data Analysis',
    description: 'Extracting and validating Schema.org markup',
    estimatedDuration: 2,
    status: 'pending'
  },
  {
    id: 'accessibility',
    name: 'Accessibility Check',
    description: 'Evaluating WCAG compliance and inclusive design',
    estimatedDuration: 2,
    status: 'pending'
  },
  {
    id: 'psi',
    name: 'PageSpeed Insights',
    description: 'Fetching Core Web Vitals and performance metrics',
    estimatedDuration: 6,
    status: 'pending'
  },
  {
    id: 'eat',
    name: 'E-A-T Analysis',
    description: 'Evaluating Expertise, Authority, and Trust signals',
    estimatedDuration: 3,
    status: 'pending'
  },
  {
    id: 'ai-optimization',
    name: 'AI Optimization Analysis',
    description: 'Comprehensive AI readiness and answer engine optimization',
    estimatedDuration: 4,
    status: 'pending'
  },
  {
    id: 'finalize',
    name: 'Finalizing Report',
    description: 'Compiling results and generating insights',
    estimatedDuration: 2,
    status: 'pending'
  }
];